import React, { useState, useEffect, useRef } from 'react';
import {
  ref, push, update, onValue, get, runTransaction
} from 'firebase/database';
import {
  signInWithPopup, signInWithRedirect,
  getRedirectResult, signOut, onAuthStateChanged
} from 'firebase/auth';
import { db, auth, googleProvider } from './firebase';
import './App.css';

const HUNTING_SPOTS = [
  { id: 'redko2',     label: '빨코2',   fullName: '빨간코끼리2' },
  { id: 'deeprock2',  label: '깊바협2', fullName: '깊은바위협곡2' },
  { id: 'whitemoon3', label: '흰달산3', fullName: '흰달산3' },
  { id: 'stardust2',  label: '별똥별2', fullName: '별똥별2' },
  { id: 'snow3',      label: '눈나라3', fullName: '눈의나라3' },
  { id: 'volcano2',   label: '화산2',   fullName: '화산지대2' },
  { id: 'desert3',    label: '사막3',   fullName: '사막지대3' },
];

// 매칭 확인 제한 시간 (초)
const CONFIRM_TIMEOUT_SEC = 10;

const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

const getSessionId = () => {
  let id = localStorage.getItem('msSessionId');
  if (!id) {
    id = Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem('msSessionId', id);
  }
  return id;
};

const loadMyEntryIds = () => {
  try { return JSON.parse(localStorage.getItem('msMyEntries') || '[]'); }
  catch { return []; }
};

const saveMyEntryIds = (ids) => {
  localStorage.setItem('msMyEntries', JSON.stringify(ids));
};

export default function App() {
  const [user, setUser]                     = useState(null);
  const [userData, setUserData]             = useState(null);
  const [selectedSpot, setSelectedSpot]     = useState(HUNTING_SPOTS[0].id);
  const [shareQueue, setShareQueue]         = useState([]);
  const [waitQueue, setWaitQueue]           = useState([]);
  const [myEntryIds, setMyEntryIds]         = useState(() => loadMyEntryIds());
  const [showShareModal, setShowShareModal] = useState(false);
  const [showWaitModal, setShowWaitModal]   = useState(false);
  const [matchNotification, setMatchNotification] = useState(null);
  const [isSubmitting, setIsSubmitting]     = useState(false);

  const alreadyNotified = useRef(new Set());
  const sessionId       = getSessionId();

  // ── 내 등록 항목 관리 ──────────────────────────────────
  const addMyEntry = (spotId, type, key) => {
    const entryRef = `${spotId}|${type}|${key}`;
    setMyEntryIds(prev => {
      const next = [...prev, entryRef];
      saveMyEntryIds(next);
      return next;
    });
  };

  const removeMyEntry = (entryRef) => {
    setMyEntryIds(prev => {
      const next = prev.filter(i => i !== entryRef);
      saveMyEntryIds(next);
      return next;
    });
  };

  const parseEntryRef = (entryRef) => {
    const [spotId, type, key] = entryRef.split('|');
    return { spotId, type, key };
  };

  // ── 구글 로그인 ────────────────────────────────────────
  const handleLogin = async () => {
    try {
      if (isMobile) await signInWithRedirect(auth, googleProvider);
      else          await signInWithPopup(auth, googleProvider);
    } catch (e) {
      alert('로그인 오류: ' + e.message);
    }
  };

  // ── Firebase Auth ─────────────────────────────────────
  useEffect(() => {
    getRedirectResult(auth).catch((e) => {
      if (e) console.error('redirect 오류:', e);
    });

    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        const userRef = ref(db, `users/${u.uid}`);
        const snap    = await get(userRef);
        if (!snap.exists()) {
          await update(userRef, { vipTickets: 0, totalShares: 0 });
        }
        const unsubUser = onValue(userRef, (s) => {
          if (s.exists()) setUserData(s.val());
        });
        return unsubUser;
      } else {
        setUserData(null);
      }
    });
    return unsub;
  }, []);

  // ── 대기열 실시간 구독 ─────────────────────────────────
  useEffect(() => {
    const shareRef = ref(db, `queue/${selectedSpot}/share`);
    const waitRef  = ref(db, `queue/${selectedSpot}/wait`);

    const unsubShare = onValue(shareRef, (snap) => {
      const data = snap.val() || {};
      const list = Object.entries(data)
        .map(([id, v]) => ({ id, ...v }))
        .filter(v => v.status === 'waiting')
        .sort((a, b) => b.priority - a.priority || a.createdAt - b.createdAt);
      setShareQueue(list);
    });

    const unsubWait = onValue(waitRef, (snap) => {
      const data = snap.val() || {};
      const list = Object.entries(data)
        .map(([id, v]) => ({ id, ...v }))
        .filter(v => v.status === 'waiting')
        .sort((a, b) => b.priority - a.priority || a.createdAt - b.createdAt);
      setWaitQueue(list);
    });

    return () => { unsubShare(); unsubWait(); };
  }, [selectedSpot]);

  // ── 내 항목 매칭 감지 ─────────────────────────────────
  useEffect(() => {
    if (myEntryIds.length === 0) return;

    const unsubs = myEntryIds.map(entryRef => {
      const { spotId, type, key } = parseEntryRef(entryRef);
      if (!spotId || !type || !key) return () => {};

      const itemRef = ref(db, `queue/${spotId}/${type}/${key}`);

      return onValue(itemRef, async (snap) => {
        if (!snap.exists()) return;
        const data = snap.val();

        if (data.status === 'matched' && !alreadyNotified.current.has(entryRef)) {
          alreadyNotified.current.add(entryRef);

          const oppositeType = type === 'share' ? 'wait' : 'share';
          const matchedSnap  = await get(ref(db, `queue/${spotId}/${oppositeType}/${data.matchedWith}`));

          if (matchedSnap.exists()) {
            setMatchNotification({
              entryRef,
              myEntry:    { id: key, type, spotId, ...data },
              theirEntry: { id: data.matchedWith, type: oppositeType, spotId, ...matchedSnap.val() },
            });
          }
        }
      });
    });

    return () => unsubs.forEach(u => u && u());
  }, [myEntryIds]);

  // ── 매칭 로직 ─────────────────────────────────────────
  const tryMatch = async (newKey, myType, spot, myUserId) => {
    const oppositeType = myType === 'share' ? 'wait' : 'share';
    const oppositeRef  = ref(db, `queue/${spot}/${oppositeType}`);
    const snap         = await get(oppositeRef);
    if (!snap.exists()) return;

    const candidates = Object.entries(snap.val())
      .map(([id, v]) => ({ id, ...v }))
      .filter(v => v.status === 'waiting')
      .sort((a, b) => b.priority - a.priority || a.createdAt - b.createdAt);

    if (candidates.length === 0) return;

    const match      = candidates[0];
    const myRef      = ref(db, `queue/${spot}/${myType}/${newKey}`);
    const matchedRef = ref(db, `queue/${spot}/${oppositeType}/${match.id}`);
    const matchedAt  = Date.now();

    const result = await runTransaction(matchedRef, (current) => {
      if (!current || current.status !== 'waiting') return;
      return { ...current, status: 'matched', matchedWith: newKey, matchedAt };
    });

    if (!result.committed) return;
    await update(myRef, { status: 'matched', matchedWith: match.id, matchedAt });
  };

  // ── 나눔 등록 ─────────────────────────────────────────
  const handleShareRegister = async (nickname, channel) => {
    setIsSubmitting(true);
    try {
      const shareRef = ref(db, `queue/${selectedSpot}/share`);
      const newRef   = await push(shareRef, {
        nickname,
        channel:   parseInt(channel, 10),
        status:    'waiting',
        priority:  0,
        sessionId,
        userId:    user?.uid || null,
        createdAt: Date.now(),
      });
      addMyEntry(selectedSpot, 'share', newRef.key);
      await tryMatch(newRef.key, 'share', selectedSpot, user?.uid || null);
      setShowShareModal(false);
    } catch (e) {
      alert('등록 오류: ' + e.message);
    }
    setIsSubmitting(false);
  };

  // ── 대기 등록 ─────────────────────────────────────────
  const handleWaitRegister = async (nickname, useVipTicket) => {
    setIsSubmitting(true);
    try {
      let priority = 0;

      if (useVipTicket && user && (userData?.vipTickets ?? 0) > 0) {
        const vipRef = ref(db, `users/${user.uid}/vipTickets`);
        const result = await runTransaction(vipRef, (current) => {
          if ((current || 0) <= 0) return;
          return current - 1;
        });
        if (!result.committed) {
          alert('우대권 사용 실패. 다시 시도해주세요.');
          setIsSubmitting(false);
          return;
        }
        priority = 1;
      }

      const waitRef = ref(db, `queue/${selectedSpot}/wait`);
      const newRef  = await push(waitRef, {
        nickname,
        status:        'waiting',
        priority,
        usedVipTicket: priority === 1,
        sessionId,
        userId:        user?.uid || null,
        createdAt:     Date.now(),
      });
      addMyEntry(selectedSpot, 'wait', newRef.key);
      await tryMatch(newRef.key, 'wait', selectedSpot, user?.uid || null);
      setShowWaitModal(false);
    } catch (e) {
      alert('등록 오류: ' + e.message);
    }
    setIsSubmitting(false);
  };

  // ── 재대기 ────────────────────────────────────────────
  const handleRequeue = async (requeueEntry) => {
    try {
      const queueRef = ref(db, `queue/${requeueEntry.spotId}/${requeueEntry.type}`);
      const entryData = {
        nickname:      requeueEntry.nickname,
        status:        'waiting',
        priority:      requeueEntry.priority,
        usedVipTicket: requeueEntry.usedVipTicket || false,
        sessionId:     requeueEntry.sessionId || sessionId,
        userId:        requeueEntry.userId || null,
        // priority 999 = 맨 앞 (타임아웃으로 확인한 사람), 그 외는 현재 시각
        createdAt:     requeueEntry.priority === 999 ? 0 : Date.now(),
      };
      if (requeueEntry.type === 'share') {
        entryData.channel = requeueEntry.channel;
      }
      const newRef = await push(queueRef, entryData);
      addMyEntry(requeueEntry.spotId, requeueEntry.type, newRef.key);
      await tryMatch(newRef.key, requeueEntry.type, requeueEntry.spotId, requeueEntry.userId || null);
    } catch (e) {
      console.error('재대기 오류:', e);
    }
  };

  // ── 매칭 팝업 닫기 ────────────────────────────────────
  const handleMatchClose = (entryRef, shouldRequeue, requeueData) => {
    removeMyEntry(entryRef);
    setMatchNotification(null);
    if (shouldRequeue && requeueData) {
      handleRequeue(requeueData);
    }
  };

  const shareCount = shareQueue.length;
  const waitCount  = waitQueue.length;
  const myKeyList  = myEntryIds
    .map(parseEntryRef)
    .filter(e => e.spotId === selectedSpot)
    .map(e => e.key);

  return (
    <div className="app">
      <Header
        user={user}
        userData={userData}
        onLogin={handleLogin}
        onLogout={() => signOut(auth)}
      />

      <main className="main">
        <p className="section-title">🗺️ 사냥터 선택</p>
        <div className="spot-tabs">
          {HUNTING_SPOTS.map(s => (
            <button
              key={s.id}
              className={`spot-tab ${selectedSpot === s.id ? 'active' : ''}`}
              onClick={() => setSelectedSpot(s.id)}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="status-card">
          <h3>{HUNTING_SPOTS.find(s => s.id === selectedSpot)?.label} 현황</h3>
          <StatusMessage share={shareCount} wait={waitCount} />
          <div className="action-buttons">
            <button className="btn btn-share" onClick={() => setShowShareModal(true)}>🎁 자리 나눔</button>
            <button className="btn btn-wait"  onClick={() => setShowWaitModal(true)} >⏳ 자리 대기</button>
          </div>
        </div>

        <div className="queue-section">
          <QueueCard
            title={`🎁 나눔 대기열 (${shareCount}명)`}
            entries={shareQueue}
            myKeys={myKeyList}
          />
          <QueueCard
            title={`⏳ 자리 대기열 (${waitCount}명)`}
            entries={waitQueue}
            myKeys={myKeyList}
            showVip
          />
        </div>
      </main>

      {showShareModal && (
        <ShareModal
          spots={HUNTING_SPOTS}
          selectedSpot={selectedSpot}
          onSpotChange={setSelectedSpot}
          onSubmit={handleShareRegister}
          onClose={() => setShowShareModal(false)}
          isSubmitting={isSubmitting}
        />
      )}

      {showWaitModal && (
        <WaitModal
          spots={HUNTING_SPOTS}
          selectedSpot={selectedSpot}
          onSpotChange={setSelectedSpot}
          onSubmit={handleWaitRegister}
          onClose={() => setShowWaitModal(false)}
          isSubmitting={isSubmitting}
          user={user}
          userData={userData}
          onLogin={handleLogin}
        />
      )}

      {matchNotification && (
        <MatchNotification
          notification={matchNotification}
          onClose={handleMatchClose}
        />
      )}
    </div>
  );
}

// ── 하위 컴포넌트들 ────────────────────────────────────

function Header({ user, userData, onLogin, onLogout }) {
  return (
    <header className="header">
      <h1 className="logo">🏹 메플 자리 품앗이</h1>
      <div className="header-right">
        {user ? (
          <div className="user-info">
            <span className="vip-count">⭐ 우대권 {userData?.vipTickets ?? 0}개</span>
            <img src={user.photoURL} alt="프로필" className="user-avatar" />
            <span className="user-name">{user.displayName}</span>
            <button className="btn-logout" onClick={onLogout}>로그아웃</button>
          </div>
        ) : (
          <button className="btn-google-login" onClick={onLogin}>🔑 구글 로그인</button>
        )}
      </div>
    </header>
  );
}

function StatusMessage({ share, wait }) {
  if (share === 0 && wait === 0)
    return <p className="status-msg empty">아직 등록된 인원이 없어요 🌟</p>;
  if (share > wait)
    return <p className="status-msg share-surplus">🎁 나눔 가능! <strong>{share}명</strong> 나눔 대기 중</p>;
  if (wait > share)
    return <p className="status-msg wait-surplus">⏳ <strong>{wait}명</strong> 자리 대기 중</p>;
  return <p className="status-msg balanced">⚖️ 나눔 {share}명 / 대기 {wait}명</p>;
}

function QueueCard({ title, entries, myKeys, showVip }) {
  return (
    <div className="queue-card">
      <h4>{title}</h4>
      {entries.length === 0
        ? <p className="empty-msg">대기자 없음</p>
        : (
          <ul className="queue-list">
            {entries.map((e, i) => (
              <li key={e.id} className={`queue-item ${i === 0 ? 'first' : ''}`}>
                <span className="q-rank">#{i + 1}</span>
                <span className="q-name">
                  {myKeys.includes(e.id) ? e.nickname : `대기자${i + 1}`}
                </span>
                {showVip && e.priority >= 1 && <span className="badge vip">⭐VIP</span>}
                {myKeys.includes(e.id)         && <span className="badge mine">나</span>}
              </li>
            ))}
          </ul>
        )
      }
    </div>
  );
}

// ── 매칭 팝업 ─────────────────────────────────────────
function MatchNotification({ notification, onClose }) {
  const { entryRef, myEntry, theirEntry } = notification;
  const isShare      = myEntry.type === 'share';
  const oppositeType = isShare ? 'wait' : 'share';
  const spotLabel    = HUNTING_SPOTS.find(s => s.id === myEntry.spotId)?.label || myEntry.spotId;

  const myQueueRef    = ref(db, `queue/${myEntry.spotId}/${myEntry.type}/${myEntry.id}`);
  const theirQueueRef = ref(db, `queue/${theirEntry.spotId}/${oppositeType}/${theirEntry.id}`);

  const [phase, setPhase]             = useState('confirming');
  const [timeLeft, setTimeLeft]       = useState(CONFIRM_TIMEOUT_SEC);
  const [myConfirmed, setMyConfirmed] = useState(false);
  const [theirConfirmed, setTheirConfirmed] = useState(false);
  const [resultType, setResultType]   = useState(null);
  const [processing, setProcessing]   = useState(false);

  const phaseRef        = useRef('confirming');
  const myConfirmedRef  = useRef(false);
  const theirConfirmedRef = useRef(false);
  const handledRef      = useRef(false);

  useEffect(() => { phaseRef.current = phase; }, [phase]);

  // ── 카운트다운 ────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'confirming') return;

    const matchedAt    = myEntry.matchedAt || Date.now();
    const getRemaining = () =>
      Math.max(0, CONFIRM_TIMEOUT_SEC - Math.floor((Date.now() - matchedAt) / 1000));

    setTimeLeft(getRemaining());

    const interval = setInterval(() => {
      const rem = getRemaining();
      setTimeLeft(rem);
      if (rem <= 0) {
        clearInterval(interval);
        handleTimeout();
      }
    }, 500);

    return () => clearInterval(interval);
  }, [phase]); 

  // ── 상대방 상태 감지 ──────────────────────────────────
  useEffect(() => {
    return onValue(theirQueueRef, async (snap) => {
      if (!snap.exists()) return;
      const data = snap.val();

      // ── confirming 단계 ───────────────────────────────
      if (phaseRef.current === 'confirming') {
        if (data.status === 'match_confirmed') {
          theirConfirmedRef.current = true;
          setTheirConfirmed(true);
          if (myConfirmedRef.current) {
            await triggerInProgress();
          }
        }
        if (data.status === 'in_progress') {
          phaseRef.current = 'sharing';
          setPhase('sharing');
        }
        // 상대가 타임아웃으로 삭제됨 → 나는 확인했으므로 앞순위로 재대기
        if (data.status === 'timeout_removed' && myConfirmedRef.current && !handledRef.current) {
          handledRef.current = true;
          setResultType('timeout_requeued');
          phaseRef.current = 'result';
          setPhase('result');
        }
      }

      // ── sharing 단계 ──────────────────────────────────
      if (phaseRef.current === 'sharing') {
        // 나눔자: 대기자가 완료 눌렀을 때
        if (isShare && data.status === 'confirmed' && !handledRef.current) {
          handledRef.current = true;
          setResultType('completed_sharer');
          phaseRef.current = 'result';
          setPhase('result');
        }
        // 대기자: 나눔자가 취소했을 때
        if (!isShare && data.status === 'cancelled' && !handledRef.current) {
          handledRef.current = true;
          setResultType('cancelled_by_sharer');
          phaseRef.current = 'result';
          setPhase('result');
        }
      }
    });
  }, []);

  // ── 내 상태 감지 (in_progress 전환 대비) ──────────────
  useEffect(() => {
    return onValue(myQueueRef, (snap) => {
      if (!snap.exists()) return;
      const data = snap.val();
      if (data.status === 'in_progress' && phaseRef.current === 'confirming') {
        phaseRef.current = 'sharing';
        setPhase('sharing');
      }
    });
  }, []);

  // ── 매칭 확인 버튼 ────────────────────────────────────
  const handleConfirmMatch = async () => {
    if (processing || myConfirmedRef.current) return;
    setProcessing(true);
    myConfirmedRef.current = true;
    setMyConfirmed(true);
    try {
      await update(myQueueRef, { status: 'match_confirmed' });
      if (theirConfirmedRef.current) {
        await triggerInProgress();
      }
    } catch (e) {
      console.error(e);
    }
    setProcessing(false);
  };

  // ── 양쪽 확인 완료 → 나눔 시작 ───────────────────────
  const triggerInProgress = async () => {
    const result = await runTransaction(myQueueRef, (cur) => {
      if (!cur || cur.status !== 'match_confirmed') return;
      return { ...cur, status: 'in_progress' };
    });
    if (result.committed) {
      await update(theirQueueRef, { status: 'in_progress' });
    }
    phaseRef.current = 'sharing';
    setPhase('sharing');
  };

  // ── 타임아웃 ─────────────────────────────────────────
  const handleTimeout = async () => {
    if (phaseRef.current !== 'confirming' || handledRef.current) return;

    if (!myConfirmedRef.current) {
      // 확인 안 누름 → 대기열 삭제
      handledRef.current = true;
      await update(myQueueRef, { status: 'timeout_removed' });
      setResultType('timeout_removed');
      phaseRef.current = 'result';
      setPhase('result');
    }
    // 확인 눌렀으면 상대방 timeout_removed 감지 시 처리됨 (onValue)
  };

  // ── 나눔 완료 (대기자가 누름) ─────────────────────────
  const handleConfirmShare = async () => {
    if (processing || handledRef.current) return;
    handledRef.current = true;
    setProcessing(true);
    try {
      await update(myQueueRef,    { status: 'confirmed', confirmedAt: Date.now() });
      await update(theirQueueRef, { status: 'confirmed', confirmedAt: Date.now() });
      // 나눔자에게 우대권 지급
      if (theirEntry.userId) {
        const vipRef   = ref(db, `users/${theirEntry.userId}/vipTickets`);
        const totalRef = ref(db, `users/${theirEntry.userId}/totalShares`);
        await runTransaction(vipRef,   v => (v || 0) + 1);
        await runTransaction(totalRef, v => (v || 0) + 1);
      }
      setResultType('completed_waiter');
      phaseRef.current = 'result';
      setPhase('result');
    } catch (e) {
      handledRef.current = false;
      alert('오류: ' + e.message);
    }
    setProcessing(false);
  };

  // ── 나눔 취소 (나눔자가 누름) ─────────────────────────
  const handleCancelShare = async () => {
    if (processing || handledRef.current) return;
    handledRef.current = true;
    setProcessing(true);
    try {
      await update(myQueueRef,    { status: 'cancelled', cancelledAt: Date.now() });
      await update(theirQueueRef, { status: 'cancelled', cancelledAt: Date.now() });
      setResultType('cancelled_sharer_view');
      phaseRef.current = 'result';
      setPhase('result');
    } catch (e) {
      handledRef.current = false;
      alert('오류: ' + e.message);
    }
    setProcessing(false);
  };

  // ── 결과 팝업 닫기 ────────────────────────────────────
  const handleResultClose = () => {
    const needRequeue = resultType === 'cancelled_by_sharer' || resultType === 'timeout_requeued';
    onClose(
      entryRef,
      needRequeue,
      needRequeue
        ? { ...myEntry, priority: resultType === 'timeout_requeued' ? 999 : myEntry.priority }
        : null
    );
  };

  // ━━━ 결과 팝업 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  if (phase === 'result') {
    return (
      <div className="match-overlay">
        <div className="match-modal">
          {resultType === 'completed_waiter' && (<>
            <div className="match-icon">✅</div>
            <h2>나눔 완료!</h2>
            <p>자리 나눔이 완료되었어요 😊</p>
            <p style={{ color: 'var(--muted)', fontSize: '0.88rem', marginTop: '8px' }}>
              나눔자에게 우대권이 지급되었어요!
            </p>
          </>)}
          {resultType === 'completed_sharer' && (<>
            <div className="match-icon">⭐</div>
            <h2>우대권 지급!</h2>
            <p>대기자가 나눔을 확인했어요!</p>
            <p className="vip-reward">⭐ 우대권 1개가 지급되었어요!</p>
          </>)}
          {resultType === 'cancelled_by_sharer' && (<>
            <div className="match-icon">❌</div>
            <h2>나눔 취소</h2>
            <p>나눔자의 요청으로 취소되었어요.</p>
            <p style={{ color: 'var(--muted)', fontSize: '0.88rem', marginTop: '8px' }}>
              기존 우선순위로 대기열에 다시 등록돼요.
            </p>
          </>)}
          {resultType === 'cancelled_sharer_view' && (<>
            <div className="match-icon">❌</div>
            <h2>나눔 취소</h2>
            <p>나눔자의 요청으로 취소되었어요.</p>
          </>)}
          {resultType === 'timeout_removed' && (<>
            <div className="match-icon">⏰</div>
            <h2>시간 초과</h2>
            <p>매칭 확인 시간이 초과되었어요.</p>
            <p style={{ color: '#f87171', fontSize: '0.88rem', marginTop: '8px' }}>
              대기열에서 삭제됩니다.
            </p>
          </>)}
          {resultType === 'timeout_requeued' && (<>
            <div className="match-icon">🔄</div>
            <h2>재대기 등록</h2>
            <p>상대방이 응답하지 않아 취소되었어요.</p>
            <p style={{ color: '#a78bfa', fontSize: '0.88rem', marginTop: '8px' }}>
              대기열 맨 앞에 다시 등록돼요! 🔝
            </p>
          </>)}
          <button className="btn-match-close" onClick={handleResultClose}>확인</button>
        </div>
      </div>
    );
  }

  // ━━━ 매칭 확인 단계 (10초 카운트다운) ━━━━━━━━━━━━━━━━
  if (phase === 'confirming') {
    return (
      <div className="match-overlay">
        <div className="match-modal">
          <div className="match-icon">🎯</div>
          <h2>매칭 완료!</h2>
          <p className="match-spot">{spotLabel}</p>

          <div className={`confirm-timer ${timeLeft <= 3 ? 'urgent' : ''}`}>
            <span className="timer-number">{timeLeft}</span>
            <p className="timer-label">초 안에 확인해주세요!</p>
          </div>

          <div className="confirm-status">
            <div className={`confirm-badge ${myConfirmed ? 'done' : 'pending'}`}>
              나 {myConfirmed ? '✅' : '⏳'}
            </div>
            <div className="confirm-vs">VS</div>
            <div className={`confirm-badge ${theirConfirmed ? 'done' : 'pending'}`}>
              상대 {theirConfirmed ? '✅' : '⏳'}
            </div>
          </div>

          {!myConfirmed ? (
            <button
              className="btn-match-confirm"
              onClick={handleConfirmMatch}
              disabled={processing}
            >
              {processing ? '처리 중...' : '✅ 매칭 확인'}
            </button>
          ) : (
            <p style={{ color: 'var(--muted)', marginTop: '12px', fontSize: '0.88rem' }}>
              상대방 확인 대기 중...
            </p>
          )}

          <p className="timeout-warning">
            ⚠️ 시간 내 미확인 시 대기열에서 삭제돼요
          </p>
        </div>
      </div>
    );
  }

  // ━━━ 나눔 진행 단계 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  return (
    <div className="match-overlay">
      <div className="match-modal">
        <div className="match-icon">🎉</div>
        <h2>나눔 진행 중!</h2>
        <p className="match-spot">{spotLabel}</p>

        {isShare ? (
          // 나눔자 뷰
          <>
            <p style={{ marginBottom: '14px' }}>대기자와 매칭됐어요!</p>
            <div className="match-info">
              <div className="match-row">
                <span className="ml">대기자 닉네임</span>
                <span className="mv">{theirEntry.nickname}</span>
              </div>
            </div>
            <p className="match-notice">
              자리를 나눠주세요.<br />
              대기자가 완료 확인 시 ⭐ 우대권이 지급돼요!
            </p>
            <button
              className="btn-match-cancel"
              onClick={handleCancelShare}
              disabled={processing}
            >
              {processing ? '처리 중...' : '나눔 취소'}
            </button>
          </>
        ) : (
          // 대기자 뷰
          <>
            <p style={{ marginBottom: '14px' }}>자리 나눔을 받을 수 있어요!</p>
            <div className="match-info">
              <div className="match-row">
                <span className="ml">닉네임</span>
                <span className="mv">{theirEntry.nickname}</span>
              </div>
              <div className="match-row">
                <span className="ml">채널</span>
                <span className="mv">{theirEntry.channel}채널</span>
              </div>
            </div>
            <p className="match-notice">
              해당 채널로 이동 후 나눔을 받으셨다면<br />
              아래 버튼을 눌러주세요!
            </p>
            <button
              className="btn-match-confirm"
              onClick={handleConfirmShare}
              disabled={processing}
            >
              {processing ? '처리 중...' : '나눔 완료 ✅'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ShareModal({ spots, selectedSpot, onSpotChange, onSubmit, onClose, isSubmitting }) {
  const [nickname, setNickname] = useState('');
  const [channel, setChannel]   = useState('');

  const submit = (e) => {
    e.preventDefault();
    if (!nickname.trim())        return alert('닉네임을 입력하세요');
    if (!channel || channel < 1) return alert('채널을 입력하세요');
    onSubmit(nickname.trim(), channel);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>🎁 자리 나눔 등록</h3>
        <p className="modal-desc">닉네임과 채널은 매칭된 상대에게만 공개돼요</p>
        <form onSubmit={submit}>
          <Field label="사냥터">
            <select value={selectedSpot} onChange={e => onSpotChange(e.target.value)}>
              {spots.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="닉네임">
            <input
              placeholder="인게임 닉네임"
              value={nickname}
              onChange={e => setNickname(e.target.value)}
              maxLength={16}
            />
          </Field>
          <Field label="채널">
            <input
              type="number"
              placeholder="채널 번호 (예: 3)"
              value={channel}
              onChange={e => setChannel(e.target.value)}
              min={1}
              max={99}
            />
          </Field>
          <ModalActions onClose={onClose} label="나눔 등록" isSubmitting={isSubmitting} />
        </form>
      </div>
    </div>
  );
}

function WaitModal({ spots, selectedSpot, onSpotChange, onSubmit, onClose, isSubmitting, user, userData, onLogin }) {
  const [nickname, setNickname]         = useState('');
  const [useVipTicket, setUseVipTicket] = useState(false);
  const hasTicket = user && (userData?.vipTickets ?? 0) > 0;

  const submit = (e) => {
    e.preventDefault();
    if (!nickname.trim()) return alert('닉네임을 입력하세요');
    onSubmit(nickname.trim(), useVipTicket);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>⏳ 자리 대기 등록</h3>
        <form onSubmit={submit}>
          <Field label="사냥터">
            <select value={selectedSpot} onChange={e => onSpotChange(e.target.value)}>
              {spots.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="닉네임">
            <input
              placeholder="인게임 닉네임"
              value={nickname}
              onChange={e => setNickname(e.target.value)}
              maxLength={16}
            />
          </Field>
          <div className="vip-section">
            {!user ? (
              <div className="vip-login-req">
                <p>⭐ 우대권 사용은 로그인이 필요해요</p>
                <button
                  type="button"
                  className="btn-google-login"
                  style={{ fontSize: '0.8rem', padding: '6px 12px' }}
                  onClick={onLogin}
                >
                  구글 로그인
                </button>
              </div>
            ) : hasTicket ? (
              <label className="vip-check-label">
                <input
                  type="checkbox"
                  checked={useVipTicket}
                  onChange={e => setUseVipTicket(e.target.checked)}
                />
                ⭐ 우대권 사용 (보유: {userData.vipTickets}개) — 대기열 우선 배치
              </label>
            ) : (
              <p className="no-vip">보유 우대권 없음 — 자리 나눔을 하면 우대권을 받을 수 있어요!</p>
            )}
          </div>
          <ModalActions onClose={onClose} label="대기 등록" isSubmitting={isSubmitting} />
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="form-group">
      <label>{label}</label>
      {children}
    </div>
  );
}

function ModalActions({ onClose, label, isSubmitting }) {
  return (
    <div className="modal-actions">
      <button type="button" className="btn-cancel" onClick={onClose}>취소</button>
      <button type="submit" className="btn-submit" disabled={isSubmitting}>
        {isSubmitting ? '등록 중...' : label}
      </button>
    </div>
  );
}